import { trackLeadFormSubmit } from "#utils/utils";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Progress,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";
import {
  FaBuilding,
  FaEnvelope,
  FaMapMarkerAlt,
  FaNetworkWired,
  FaPhone,
  FaServer,
  FaShieldAlt,
  FaUser,
} from "react-icons/fa/index.js";
import { z } from "zod";

const schema = z.object({
  contactName: z.string().min(2, "Contact name is required"),
  company: z.string().min(2, "Company is required"),
  product: z.enum(["DIA", "Private Line", "MPLS", "SIP Trunk", "Other"], {
    required_error: "Product is required",
  }),
  phone: z
    .string()
    .regex(/^\d{7,}$/, "Phone must be numeric and at least 7 digits"),
  email: z.string().email("Valid email required"),
  address: z.string().min(3, "Service address is required"),
});
type Schema = z.infer<typeof schema>;

const initialForm: Schema = {
  contactName: "",
  company: "",
  product: "DIA",
  phone: "",
  email: "",
  address: "",
};

const PRODUCT_CARDS = [
  { id: "DIA", label: "Fiber / DIA", icon: FaNetworkWired },
  { id: "SIP Trunk", label: "Voice / SIP", icon: FaPhone },
  { id: "MPLS", label: "Network / MPLS", icon: FaServer },
  { id: "Other", label: "Other / Security", icon: FaShieldAlt },
] as const;

const LeadGen = () => {
  const [formData, setFormData] = useState<Schema>(() => {
    try {
      const email = localStorage.getItem("prefill_email") || "";
      const company = localStorage.getItem("prefill_company") || "";
      return { ...initialForm, email, company };
    } catch {
      return initialForm;
    }
  });

  const [errors, setErrors] = useState<Partial<Record<keyof Schema, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const toast = useToast();

  // Colors
  const cardShadow = useColorModeValue("xl", "2xl");
  const cardBg = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.900", "gray.200");
  const inputBg = useColorModeValue("gray.50", "gray.700");
  const unselectedCardBg = useColorModeValue("gray.50", "gray.700");
  const selectedCardBg = useColorModeValue("blue.50", "blue.900");
  const selectedCardBorder = useColorModeValue("blue.500", "blue.300");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleProductSelect = (productId: string) => {
    setFormData((prev) => ({ ...prev, product: productId as any }));
    setErrors((prev) => ({ ...prev, product: undefined }));
    // Auto-advance after a short delay for tactile response
    setTimeout(() => nextStep(), 300);
  };

  const validateStep = (currentStep: number): boolean => {
    const newErrors: Partial<Record<keyof Schema, string>> = {};
    let isValid = true;

    if (currentStep === 1) {
      if (!formData.product) {
        newErrors.product = "Please select a product.";
        isValid = false;
      }
    } else if (currentStep === 2) {
      if (!formData.company || formData.company.length < 2) {
        newErrors.company = "Company name is required";
        isValid = false;
      }
      if (!formData.address || formData.address.length < 3) {
        newErrors.address = "Service address is required";
        isValid = false;
      }
    }

    if (!isValid) {
      setErrors(newErrors);
    }
    return isValid;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep((p) => Math.min(p + 1, 3));
    }
  };

  const prevStep = () => setStep((p) => Math.max(p - 1, 1));

  // --- Zoho CRM API integration ---
  async function sendToZohoCRM(formData: Schema): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch("/.netlify/functions/submit-to-zoho", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await response.json();
      if (response.ok) {
        if (
          data.message === "Successfully submitted to Zoho!" ||
          (data.result?.data?.[0]?.code === "SUCCESS")
        ) {
          return { success: true, message: "Lead sent to Zoho CRM successfully!" };
        } else {
          return { success: false, message: data.result?.data?.[0]?.message || data.message || "Unknown error." };
        }
      } else {
        return { success: false, message: data.message || `Server error: ${response.status}` };
      }
    } catch (error) {
      return { success: false, message: "Network error. Please check your connection." };
    }
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    try {
      schema.parse(formData);
      setIsSubmitting(true);
      const result = await sendToZohoCRM(formData);

      setIsSubmitting(false);
      if (result.success) {
        setFormData(initialForm);
        setStep(1);
        toast({
          title: "Quote request sent!",
          description: "Our engineering team will be in touch shortly.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        trackLeadFormSubmit({
          form_name: "lead_gen_wizard",
          lead_type: "quote_request",
          product: formData.product,
          company: formData.company,
          source: "multi_step_form",
        });
      } else {
        toast({
          title: "Submission failed",
          description: result.message,
          status: "error",
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      setIsSubmitting(false);
      if (err.errors) {
        const fieldErrors: Partial<Record<keyof Schema, string>> = {};
        err.errors.forEach((e: any) => {
          fieldErrors[e.path[0] as keyof Schema] = e.message;
        });
        setErrors(fieldErrors);
      }
    }
  };

  const animationProps = {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { duration: 0.3 }
  };

  return (
    <Box
      bg={cardBg}
      boxShadow={cardShadow}
      borderRadius="2xl"
      p={{ base: 6, md: 8 }}
      w="full"
      maxW="md"
      mx="auto"
      color={textColor}
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      position="relative"
    >
      <Progress
        value={(step / 3) * 100}
        size="xs"
        colorScheme="blue"
        position="absolute"
        top={0}
        left={0}
        right={0}
        bg="transparent"
      />

      <Heading as="h2" size="lg" mb={2} textAlign="center" pt={2}>
        Compare Provider Rates
      </Heading>
      <Text fontSize={{ base: "sm", md: "md" }} color="gray.500" mb={6} textAlign="center">
        Step {step} of 3
      </Text>

      <Box as="form" onSubmit={(e) => { e.preventDefault(); if (step === 3) handleSubmit(); }} id="lead_gen_wizard">
        <AnimatePresence mode="wait">

          {/* STEP 1: OBJECTIVE / PRODUCT */}
          {step === 1 && (
            <motion.div key="step1" {...animationProps}>
              <VStack spacing={5} align="stretch">
                <Text fontWeight="bold" textAlign="center">What solution are you exploring?</Text>
                <SimpleGrid columns={2} spacing={4}>
                  {PRODUCT_CARDS.map((prod) => {
                    const isSelected = formData.product === prod.id || (prod.id === 'Other' && !['DIA', 'SIP Trunk', 'MPLS'].includes(formData.product));
                    return (
                      <Box
                        key={prod.id}
                        as="button"
                        type="button"
                        onClick={() => handleProductSelect(prod.id)}
                        p={4}
                        borderRadius="xl"
                        border="2px solid"
                        borderColor={isSelected ? selectedCardBorder : borderColor}
                        bg={isSelected ? selectedCardBg : unselectedCardBg}
                        _hover={{ borderColor: selectedCardBorder, transform: "translateY(-2px)" }}
                        transition="all 0.2s"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={3}
                      >
                        <Icon as={prod.icon} w={6} h={6} color={isSelected ? "blue.500" : "gray.500"} />
                        <Text fontSize="sm" fontWeight={isSelected ? "bold" : "medium"}>
                          {prod.label}
                        </Text>
                      </Box>
                    );
                  })}
                </SimpleGrid>
                {errors.product && <Text color="red.500" fontSize="sm">{errors.product}</Text>}
                <Button rightIcon={<Icon as={FaBuilding} />} colorScheme="blue" size="lg" w="full" mt={2} onClick={nextStep}>
                  Continue
                </Button>
              </VStack>
            </motion.div>
          )}

          {/* STEP 2: COMPANY INFO */}
          {step === 2 && (
            <motion.div key="step2" {...animationProps}>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold" textAlign="center">Tell us about your organization.</Text>

                <FormControl isInvalid={!!errors.company} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Company Name</FormLabel>
                  <HStack bg={inputBg} borderRadius="md" px={3} border="1px solid" borderColor={borderColor}>
                    <Icon as={FaBuilding} color="gray.400" />
                    <Input name="company" variant="unstyled" py={3} value={formData.company} onChange={handleChange} placeholder="Acme Corp" />
                  </HStack>
                  <FormErrorMessage>{errors.company}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.address} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Service Address (City, State, or Zip)</FormLabel>
                  <HStack bg={inputBg} borderRadius="md" px={3} border="1px solid" borderColor={borderColor}>
                    <Icon as={FaMapMarkerAlt} color="gray.400" />
                    <Input name="address" variant="unstyled" py={3} value={formData.address} onChange={handleChange} placeholder="Atlanta, GA" />
                  </HStack>
                  <FormErrorMessage>{errors.address}</FormErrorMessage>
                </FormControl>

                <Flex gap={3} mt={4}>
                  <Button variant="outline" w="30%" onClick={prevStep}>Back</Button>
                  <Button colorScheme="blue" w="70%" onClick={nextStep}>Next: Contact Details</Button>
                </Flex>
              </VStack>
            </motion.div>
          )}

          {/* STEP 3: CONTACT & SUBMIT */}
          {step === 3 && (
            <motion.div key="step3" {...animationProps}>
              <VStack spacing={4} align="stretch">
                <Text fontWeight="bold" textAlign="center">Where should we send your quotes?</Text>

                <FormControl isInvalid={!!errors.contactName} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Full Name</FormLabel>
                  <HStack bg={inputBg} borderRadius="md" px={3} border="1px solid" borderColor={borderColor}>
                    <Icon as={FaUser} color="gray.400" />
                    <Input name="contactName" variant="unstyled" py={3} value={formData.contactName} onChange={handleChange} placeholder="Jane Doe" />
                  </HStack>
                  <FormErrorMessage>{errors.contactName}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.email} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Work Email</FormLabel>
                  <HStack bg={inputBg} borderRadius="md" px={3} border="1px solid" borderColor={borderColor}>
                    <Icon as={FaEnvelope} color="gray.400" />
                    <Input type="email" name="email" variant="unstyled" py={3} value={formData.email} onChange={handleChange} placeholder="jane@acme.com" />
                  </HStack>
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.phone} isRequired>
                  <FormLabel fontSize="sm" fontWeight="semibold">Direct Phone</FormLabel>
                  <HStack bg={inputBg} borderRadius="md" px={3} border="1px solid" borderColor={borderColor}>
                    <Icon as={FaPhone} color="gray.400" />
                    <Input name="phone" inputMode="numeric" variant="unstyled" py={3} value={formData.phone} onChange={handleChange} placeholder="555-012-3456" />
                  </HStack>
                  <FormErrorMessage>{errors.phone}</FormErrorMessage>
                </FormControl>

                <Flex gap={3} mt={4}>
                  <Button variant="outline" w="30%" onClick={prevStep} isDisabled={isSubmitting}>Back</Button>
                  <Button type="submit" colorScheme="green" w="70%" isLoading={isSubmitting} loadingText="Encrypting...">
                    Get Instant Quotes
                  </Button>
                </Flex>
                <Text fontSize="xs" color="gray.500" textAlign="center" mt={2}>
                  Your information is securely encrypted and never sold.
                </Text>
              </VStack>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default LeadGen;
